import {
  deriveSupportProfile,
  resolvePricingProfile,
  type CatalogProduct,
  type CustomerType,
  type CustomerDesignation,
  type PricingProfileConfig,
  type ResolvedPricingProfile,
  type JobCreateRequest,
  type JobCreateLineItem,
  type TemplateTaskConfig,
  type TaskListDefinition,
  type TaskDropdownDefinition,
} from '@techgitter/contracts';
import type { LineItem } from '../components/tickets/LineItemCard';
import type {
  TicketTypeTemplate,
  TemplateCustomerContext,
  AdditionalDetailsValues,
} from '../types/ticketTypeTemplate';
import { generateLineItemsFromTemplate, interpolateTemplate } from './templateEngine';
import { buildProductSkuIndex, getProductFromIndex } from '../hooks/useProductCatalog';
import { resolveChecklist, snapshotChecklist, buildSavedAudit } from './checklistEngine';

/**
 * Shared job-payload assembly used by both the interactive ticket-entry flow
 * (`TicketEntryPage`) and the headless recurring-ticket generator
 * (`RecurringTodoPanel`). Extracted so the two paths can't drift on how a
 * template + line items become a `JobCreateRequest`.
 */

/** Map already-materialised form line items → the job-create line-item shape.
 *  This is the exact mapping `TicketEntryPage.buildJobPayload` performs; both
 *  callers share it so the SKU/quantity/productId contract stays in one place.
 *
 *  `products` is indexed case-insensitively and the matched product's own `sku`
 *  is what goes out — the catalog's casing is not self-consistent (see
 *  `normalizeSku`), and an unmatched SKU costs the RS line its product link. */
export function mapLineItemsToJobLineItems(
  lineItems: LineItem[],
  products: CatalogProduct[],
): JobCreateLineItem[] {
  const index = buildProductSkuIndex(products);

  return lineItems
    .filter((item) => item.lineItemId && item.quantity > 0)
    .map((item) => {
      const product = getProductFromIndex(index, item.lineItemId);
      return {
        sku: product?.sku ?? item.lineItemId,
        quantity: item.quantity,
        unitCost: item.unitCost,
        description: item.lineDetail,
        afterHours: item.afterHours,
        nonChargeable: item.nonChargeable,
        productId: product?.productId,
      };
    });
}

/** Minimal customer descriptor the recurring generator has on hand (sourced
 *  from the customer index). Enough to derive support + designation-based
 *  pricing; per-license pricing (which needs a renewal schedule) is out of
 *  scope because those templates carry required license-picker fields and are
 *  excluded from recurring tickets. */
export interface TemplateContextCustomer {
  dataverseId: string;
  repairShoprId: string;
  customerType?: CustomerType;
  /** Raw designation string off the customer index ('Core' | 'Core+' | …). */
  designation?: string;
  city?: string;
}

const DESIGNATION_MAP: Record<string, CustomerDesignation> = {
  Core: 'Core',
  'Core+': 'CorePlus',
  CorePlus: 'CorePlus',
};

/** Build the lean `TemplateCustomerContext` needed to price a template's line
 *  items. Mirrors `TicketEntryPage`'s customer-context memo, minus the
 *  service-flag / renewal-schedule fields that only drive excluded templates. */
export function buildTemplateCustomerContext(
  customer: TemplateContextCustomer,
  products: CatalogProduct[],
  pricingProfilesConfig: PricingProfileConfig | null | undefined,
): TemplateCustomerContext {
  const customerType: CustomerType = customer.customerType ?? 'Business';
  const designation: CustomerDesignation = customer.designation
    ? DESIGNATION_MAP[customer.designation] ?? undefined
    : undefined;

  let resolvedPricingProfile: ResolvedPricingProfile | undefined;
  if (pricingProfilesConfig && products.length > 0) {
    resolvedPricingProfile = resolvePricingProfile(
      pricingProfilesConfig,
      customerType,
      designation,
      products,
    );
  }

  return {
    customerId: customer.dataverseId,
    designation,
    supportProfile: deriveSupportProfile({ customerType, designation }),
    resolvedPricingProfile,
    repairShoprCustomerId: Number(customer.repairShoprId) || undefined,
    city: customer.city,
  };
}

export interface BuildJobPayloadFromTemplateArgs {
  template: TicketTypeTemplate;
  customer: TemplateContextCustomer;
  status: 'new' | 'resolved';
  products: CatalogProduct[];
  pricingProfilesConfig: PricingProfileConfig | null | undefined;
  /** ISO date the ticket is dated. For recurring tickets the generator
   *  re-stamps this to the actual run date, so a placeholder is fine. */
  ticketDate: string;
  /** Values for the template's additional fields. Recurring tickets are
   *  restricted to templates with no required additional fields, so this is
   *  typically empty. */
  additionalDetails?: AdditionalDetailsValues;
  /** The template's checklist binding (cr001_template_task_configses), matched
   *  by ticketTypeId. When present its checklist is materialised and snapshotted
   *  into dvOverlay.cr001_task_list so the generated ticket carries the task
   *  list — mirroring the interactive TicketEntryPage flow. */
  taskConfig?: TemplateTaskConfig | null;
  /** Task-list catalog, needed only to resolve nested task-list references. */
  taskListCatalog?: TaskListDefinition[];
  /** Reusable dropdown option-sets, needed only if the checklist has dropdown tasks. */
  taskDropdowns?: TaskDropdownDefinition[];
  /** Previously-snapshotted line items to reuse verbatim instead of
   *  re-materialising from the template. The recurring editor passes this when
   *  re-saving an existing definition whose customer is unchanged, so an edit
   *  that only touches the checklist/schedule can't silently regenerate — and
   *  drift — the line items off a template that has since changed. Omit (the
   *  interactive flow and new definitions) to materialise fresh. */
  lineItemsOverride?: JobCreateLineItem[];
  /** Reference instant for date/time-sensitive pricing rules (e.g. support
   *  labour's after-hours window). Kept separate from `now` because a recurring
   *  snapshot is replayed on every run: the caller pins this to a standard
   *  business-hours instant (see `standardBusinessHoursReference`) so line items
   *  are priced at the standard rate rather than frozen to the save-time window.
   *  Defaults to `now`. */
  pricingNow?: Date;
  /** Injected for deterministic tests; defaults to now at call time. Also
   *  stamps the checklist snapshot timestamp. */
  now?: Date;
}

/**
 * Resolve a ticket-type template into a concrete `JobCreateRequest`:
 * materialise its line items (interpolation + pricing), interpolate the
 * subject/description, and assemble the job payload. Pure w.r.t. its inputs.
 */
export function buildJobPayloadFromTemplate({
  template,
  customer,
  status,
  products,
  pricingProfilesConfig,
  ticketDate,
  additionalDetails = {},
  taskConfig,
  taskListCatalog,
  taskDropdowns,
  lineItemsOverride,
  pricingNow,
  now,
}: BuildJobPayloadFromTemplateArgs): JobCreateRequest {
  const customerContext = buildTemplateCustomerContext(customer, products, pricingProfilesConfig);

  // Reuse the caller's line items when preserving an existing snapshot;
  // otherwise materialise them fresh from the template. Date/time-sensitive
  // pricing rules see `pricingNow` (pinned to business hours for recurring
  // snapshots), falling back to `now`.
  const lineItems = lineItemsOverride ?? mapLineItemsToJobLineItems(
    generateLineItemsFromTemplate(template, additionalDetails, customerContext, undefined, pricingNow ?? now),
    products,
  );

  const subject = template.subjectTemplate
    ? interpolateTemplate(template.subjectTemplate, additionalDetails)
    : template.displayName;
  const description = template.descriptionTemplate
    ? interpolateTemplate(template.descriptionTemplate, additionalDetails)
    : template.defaultTicketDescription ?? '';

  const dvOverlay: Record<string, string> = {
    cr001_template_field_values: JSON.stringify(additionalDetails),
    // `description` below only reaches RepairShopr; cr001_description is what
    // the edit screen reads back, so a generated ticket has to carry it too or
    // it opens with a blank Description.
    cr001_description: description,
  };

  // Attach the template's checklist, if bound. Materialise it the same way the
  // interactive flow does and store the SavedChecklist snapshot — the
  // orchestrator merges dvOverlay verbatim onto the job and never resolves a
  // task-list id server-side, so the checklist must be fully materialised here.
  if (taskConfig) {
    const { checklist } = resolveChecklist(
      taskConfig,
      taskListCatalog ?? [],
      additionalDetails,
      customerContext,
      taskDropdowns ?? [],
    );
    if (checklist.sections.length > 0) {
      const snapshotAt = (now ?? new Date()).toISOString();
      dvOverlay.cr001_task_list = JSON.stringify(snapshotChecklist(checklist, snapshotAt));
      dvOverlay.cr001_task_audit = JSON.stringify(buildSavedAudit([]));
    }
  }

  const request: JobCreateRequest = {
    customer: {
      dataverseId: customer.dataverseId,
      repairShoprId: customer.repairShoprId,
    },
    subject,
    description,
    jobType: template.group,
    ticketDate,
    status,
    lineItems,
    dvOverlay,
  };

  return request;
}
